<ng-container *transloco="let t">
  @for (step of form().steps; track step; let stepIndex = $index) {
  <div class="mb-6">
    <!-- Step group accordion -->
    <div class="rounded-lg overflow-visible">
      <!-- Group header -->
      <div
        class="flex flex-wrap items-center px-5 py-4 rounded-lg gap-3"
        [class]="
          expandedSteps().has(step)
            ? 'bg-surface-subtle rounded-b-none border border-b border-stroke-soft'
            : 'bg-surface-card border border-transparent'
        "
      >
        <!-- Left: chevron + ordinal label + count -->
        <div
          class="flex items-center gap-2 cursor-pointer select-none flex-1 min-w-0 order-1"
          (click)="toggleStepExpansion(step)"
          (keydown.enter)="toggleStepExpansion(step)"
          tabindex="0"
        >
          <mat-icon
            svgIcon="alt-arrow-right-outline"
            class="size-5! text-ink-muted! transition-transform duration-200 shrink-0"
            [class.rotate-90]="expandedSteps().has(step)"
          ></mat-icon>
          <span class="text-base sm-md:text-[1.125rem] font-medium text-ink-secondary whitespace-nowrap">
            {{ t('CONTRACT_ORDER.RECEIVE') }} {{ stepIndex + 1 | ordinal }}
          </span>
          <span class="text-xs font-normal text-ink-muted hidden sm:inline">
            {{ step.recipients.length | translocoDecimal }}
            {{ t('CONTRACT_ORDER.RECIPIENTS_LABEL') }}
          </span>
          @if (step().touched() && step().invalid()) {
          <mat-icon class="size-4! text-danger!" svgIcon="danger-triangle-bold"></mat-icon>
          }
        </div>

        <!-- 3-dot menu: stays beside the title on mobile, moves to the right on desktop -->
        <div class="order-2 sm:order-3 flex items-center gap-2">
          <span class="text-xs font-normal text-ink-muted whitespace-nowrap sm:hidden">
            {{ step.recipients.length }}
            {{ t('CONTRACT_ORDER.RECIPIENTS_LABEL') }}
          </span>
          <div class="relative">
            <button
              type="button"
              (click)="$event.stopPropagation()"
              (keydown)="$event.stopPropagation()"
              [junoLibsUiDropdownMenu]="groupDropdown"
              class="cursor-pointer size-5 flex items-center justify-center leading-none"
            >
              <mat-icon class="size-5! text-ink-muted!" svgIcon="options"></mat-icon>
            </button>
            <juno-libs-ui-dropdown-menu #groupDropdown appliedClasses="inset-e-0 top-7">
              <ul>
                @if (form().steps.length > 1 && stepIndex !== 0) {
                <li
                  (click)="moveOrderStepUp(step); groupDropdown.hideMenu()"
                  (keydown)="moveOrderStepUp(step); groupDropdown.hideMenu()"
                  tabindex="0"
                  class="w-36"
                >
                  {{ t('CONTRACT_ORDER.MOVE_UP') }}
                </li>
                } @if (form().steps.length > 1 && stepIndex !== form().steps.length - 1) {
                <li
                  (click)="moveOrderStepDown(step); groupDropdown.hideMenu()"
                  (keydown)="moveOrderStepDown(step); groupDropdown.hideMenu()"
                  tabindex="0"
                  class="w-36"
                >
                  {{ t('CONTRACT_ORDER.MOVE_DOWN') }}
                </li>
                } @if (form().steps.length > 1) {
                <li
                  (click)="removeOrderStep(step); groupDropdown.hideMenu()"
                  (keydown)="removeOrderStep(step); groupDropdown.hideMenu()"
                  tabindex="0"
                  class="text-danger w-36"
                >
                  {{ t('CONTRACT_ORDER.DELETE') }}
                </li>
                }
              </ul>
            </juno-libs-ui-dropdown-menu>
          </div>
        </div>

        <!-- Add Recipient button: full-width row under the title on mobile, inline on desktop -->
        <dss-libs-ui-button
          class="order-3 sm:order-2 w-full sm:w-auto"
          variant="outline"
          color="primary"
          label="CONTRACT_ORDER.ADD_RECEIVER"
          [iconMetaData]="addRecipientIcon"
          (clickOutput)="addRecipientToStep(step)"
        ></dss-libs-ui-button>
      </div>

      <!-- Group body -->
      @if (expandedSteps().has(step)) {
      <div class="bg-surface-card border border-t-0 border-stroke-soft rounded-b-[8px] px-5 py-6 flex flex-col gap-4">
        <div>
          @if (step.recipients.length === 0) {
          <!-- Empty state -->
          <div class="flex flex-col items-center justify-center h-[180px] gap-3">
            <mat-icon class="text-ink-subtle! size-12!" svgIcon="user-circle-outline"></mat-icon>
            <p class="text-sm font-semibold text-ink-muted text-center">{{ t('CONTRACT_ORDER.NO_RECIPIENTS') }}</p>
          </div>
          } @else {
          <!-- Individual recipient cards -->
          @for (recipient of step.recipients; let recipientIndex = $index; track recipient) {
          <div class="bg-white rounded-[12px] p-4 mb-4">
            <!-- Card header -->
            <div class="flex items-center justify-between">
              <div
                class="flex items-center gap-2 cursor-pointer select-none flex-1"
                (click)="toggleRecipientExpansion(recipient)"
                (keydown.enter)="toggleRecipientExpansion(recipient)"
                tabindex="0"
              >
                <svg
                  class="size-4 text-ink-secondary transition-transform duration-200 shrink-0"
                  [class.rotate-90]="expandedRecipients().has(recipient)"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div class="flex items-center gap-2">
                  <div class="size-7 rounded-full bg-primary-tint flex items-center justify-center shrink-0">
                    <span class="text-xs font-medium text-primary leading-none">
                      {{ recipientIndex + 1 | translocoDecimal : { minimumIntegerDigits: 2 } }}
                    </span>
                  </div>
                  <span class="text-base font-semibold text-ink-secondary">
                    {{ t('CONTRACT_ORDER.RECIPIENT_DETAILS') }}
                  </span>
                </div>
              </div>
              <!-- Trash button -->
              @if (stepIndex !== 0 || step.recipients.length > 1) {
              <button
                type="button"
                (click)="removeRecipientFromStep(recipient)"
                class="text-danger-hover cursor-pointer p-1 hover:opacity-70 transition-opacity shrink-0"
              >
                <mat-icon class="size-5!" svgIcon="trash-bin-minimalistic"></mat-icon>
              </button>
              }
            </div>

            <!-- Card body (expanded) -->
            @if (expandedRecipients().has(recipient)) {
            <div class="mt-4 pt-4 border-t border-stroke-soft">
              <div class="grid grid-cols-1 sm-md:grid-cols-2 gap-4">
                <!-- Name -->
                <div class="flex flex-col gap-2">
                  <juno-libs-ui-input [formField]="recipient.name" label="CONTRACT_ORDER.NAME" />
                </div>

                <!-- Email -->
                <div class="flex flex-col gap-2">
                  <juno-libs-ui-input type="email" [formField]="recipient.email" label="CONTRACT_ORDER.EMAIL" />
                </div>

                <!-- Phone -->
                <div class="flex flex-col gap-2">
                  <juno-libs-ui-phone-number [formField]="recipient.phone" label="CONTRACT_ORDER.PHONE_NUMBER" />
                </div>

                <!-- Role -->
                <div class="flex flex-col gap-2">
                  <juno-libs-ui-select
                    label="CONTRACT_ORDER.ROLE"
                    [formField]="recipient.task"
                    [options]="taskOptions"
                  />
                </div>
              </div>
            </div>
            }
          </div>
          } }
        </div>
      </div>
      }
    </div>
  </div>
  }

  <!-- Receive Next button -->
  <div class="mt-2">
    <button (click)="addOrderStep()" [disabled]="isReceiveButtonDisabled()" class="receive-next-button" type="button">
      <svg
        class="inline-block me-1 shrink-0"
        fill="none"
        height="16"
        viewBox="0 0 16 16"
        width="16"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clip-rule="evenodd"
          d="M1.5 1.5C1.22386 1.5 1 1.72386 1 2V6.8C1 8.18071 2.11929 9.3 3.5 9.3H13.2929L9.94645 12.6464C9.75118 12.8417 9.75118 13.1583 9.94645 13.3536C10.1417 13.5488 10.4583 13.5488 10.6536 13.3536L14.8536 9.15355C15.0488 8.95829 15.0488 8.64171 14.8536 8.44645L10.8536 4.44645C10.6583 4.25118 10.3417 4.25118 10.1464 4.44645C9.95118 4.64171 9.95118 4.95829 10.1464 5.15355L13.2929 8.3H3.5C2.67157 8.3 2 7.62843 2 6.8V2C2 1.72386 1.77614 1.5 1.5 1.5Z"
          fill="currentColor"
          fill-rule="evenodd"
        />
      </svg>
      <span>{{ t('CONTRACT_ORDER.RECEIVE_NEXT') }}</span>
    </button>
  </div>
</ng-container>
